import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, animate } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import ParticleField from "./ParticleField";

const LINES = ["TRANSFORM", "MEDICAL IMAGING", "WITH AI."];
const HERO_IMG =
  "https://images.unsplash.com/photo-1743657166981-8d8e11d03c3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMGFic3RyYWN0JTIwbWVkaWNhbCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg5MTAyNTAyfDA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { target: 99.2, decimals: 1, suffix: "%", label: "Detection sensitivity" },
  { target: 9, prefix: "<", suffix: "s", label: "Per-scan inference" },
  { target: 4, pad: 2, label: "Imaging modalities" },
  { target: 24, suffix: "/7", label: "Autonomous triage" },
];

function AnimatedStat({ stat, delay }) {
  const [val, setVal] = useState(null);
  useEffect(() => {
    const controls = animate(0, stat.target, {
      duration: 1.8,
      delay,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [stat.target, delay]);

  let text = " ";
  if (val !== null) {
    const core = stat.decimals
      ? val.toFixed(stat.decimals)
      : String(Math.round(val)).padStart(stat.pad || 1, "0");
    text = `${stat.prefix || ""}${core}${stat.suffix || ""}`;
  }
  return <div className="font-display text-3xl md:text-4xl font-bold tabular-nums">{text}</div>;
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink" data-testid="hero-section">
      <ParticleField className="absolute inset-0 w-full h-full opacity-30" />
      <div className="absolute inset-0 scanline" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1440px] mx-auto w-full px-6 md:px-12 pt-32 pb-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-8"
          data-testid="hero-eyebrow"
        >
          Clinical-grade diagnostic intelligence
        </motion.p>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-[13vw] md:text-[8.5vw]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-brand" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-mute text-base leading-relaxed"
            data-testid="hero-subcopy"
          >
            Deep-learning diagnostics across X-ray, CT, MRI and ultrasound — flagging tumors in seconds, not days. Built for radiologists, trusted by oncologists.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap gap-3"
          >
            <Link
              to="/register"
              data-testid="hero-cta-access"
              className="flex items-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-full hover:bg-[#0F172A] hover:text-white transition-colors duration-300"
            >
              Access the platform <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="#manifesto"
              data-testid="hero-cta-science"
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-900 font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-full hover:border-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-[background-color,color,border-color] duration-300"
            >
              Explore the science <ArrowDown className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 md:px-12 pb-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative border border-slate-200 rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(15,23,42,0.05)]"
          data-testid="hero-image-frame"
        >
          <motion.img
            style={{ y: imgY }}
            src={HERO_IMG}
            alt="Flowing clinical light-lines visualized by the X-Gen engine"
            className="w-full h-[42vh] md:h-[52vh] object-cover scale-110 max-h-[80vh]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" aria-hidden="true" />
          <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            NEURAL_MESH // SUBJECT 0847 — INFERENCE COMPLETE
          </div>
          <div className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
            CONFIDENCE 0.987
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 border-t-0"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <div key={s.label} className={`p-6 ${i > 0 ? "border-l border-slate-200" : ""} ${i > 1 ? "max-md:border-t max-md:border-slate-200" : ""} ${i === 2 ? "max-md:border-l-0" : ""}`}>
              <AnimatedStat stat={s} delay={1.7 + i * 0.15} />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-2">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
